import { useState } from 'react';
import _ from 'lodash';

interface Validation {
  required?: {
    value: boolean;
    message?: string;
  };
  pattern?: {
    value: string;
    message?: string;
  };
  custom?: {
    isValid: (value: any) => boolean;
    message?: string;
  };
}

export type ErrorRecord<T> = Partial<Record<keyof T, { error: boolean; errorMessage?: string }>>;

export type Validations<T extends object> = Partial<Record<keyof T, Validation>>;

export const useForm = <T extends Record<keyof T, any> = object>(options?: {
  validations?: Validations<T>;
  initialValues?: Partial<T>;
}) => {
  const [formData, setFormData] = useState<T>((options?.initialValues || {}) as T);
  const [errors, setErrors] = useState<ErrorRecord<T>>({});

  /**
   * Change handler with an optional sanitizeFn to change format of data
   */
  const handleChange = <S>(key: keyof T, value: any, sanitizeFn?: (value: any) => S) => {
    setFormData({
      ...formData,
      [key]: sanitizeFn ? sanitizeFn(value) : value,
    });
    if (errors[key]) setErrors({ ...errors, [key]: undefined });
  };

  /**
   * Reset form to inital state
   * */
  const resetForm = () => {
    setFormData((options?.initialValues || {}) as T);
    setErrors({});
  };

  /**
   * Reset field to initial state
   * */
  const resetFormField = (key: keyof T) => {
    handleChange(key, options?.initialValues?.[key] || undefined);
  };

  /**
   * Set all form values
   * */
  const setFormValues = (newFormValues: T) => {
    setFormData(newFormValues);
  };

  /**
   * Submit handler
   */
  const validateForm = () => {
    // validate data
    const validations = options?.validations;
    if (validations) {
      let valid = true;
      const newErrors: ErrorRecord<T> = {};
      for (const key in validations) {
        newErrors[key] = {
          error: false,
          errorMessage: undefined,
        };

        const value = formData[key];
        const validation = validations[key];
        if (
          validation?.required?.value &&
          (_.isUndefined(value) ||
            (_.isArrayLike(value) && _.isEmpty(value)) ||
            (typeof value == 'string' && !value.trim()))
        ) {
          valid = false;
          newErrors[key] = {
            error: true,
            errorMessage: validation?.required?.message,
          };
        }

        const pattern = validation?.pattern;
        if (pattern?.value && !RegExp(pattern.value).test(value)) {
          valid = false;
          newErrors[key] = { error: true, errorMessage: pattern.message || '' };
        }

        const custom = validation?.custom;
        if (custom?.isValid && !custom.isValid(value)) {
          valid = false;
          newErrors[key] = { error: true, errorMessage: custom.message || '' };
        }
      }

      setErrors(newErrors);
      if (!valid) {
        return false;
      }
    }

    // reset errors and submit if data is valid
    return true;
  };

  return {
    formData,
    handleChange,
    validateForm,
    resetForm,
    resetFormField,
    setFormValues,
    errors,
  };
};

export default useForm;
